import React from 'react';
import PropTypes from 'prop-types';
import { reduxForm, Field, formValueSelector, change } from 'redux-form';
import { connect } from 'react-redux';
import { get } from 'lodash';
import validateLightingForm from './forms/validateLightingForm';
import onSubmitProductionForms from './forms/onSubmitProductionForms';
import ToggleField from '../app-main/components/reduxForms/fields/ToggleField/ToggleField';
import TextAreaField from 'shared-components/reduxForms/fields/TextAreaField/TextAreaField';
import { mapBoolToString } from './data/utils';
import ButtonNavigation from '../app-faq-components/ButtonNavigation';
import { defineMessages, FormattedMessage, injectIntl, intlShape } from 'react-intl';

const LightingForm = ({
  error,
  handleSubmit,
  hasNaturalLight,
  intl,
  ...props
}) => {
  const translationMessages = defineMessages({
    yes: {
      id: 'hosting.lighting_form.yes',
      defaultMessage: 'Yes'
    },
    no: {
      id: 'hosting.lighting_form.no',
      defaultMessage: 'No'
    }
  });
  return (
    <form className="flex-basis-100" onSubmit={handleSubmit}>
      <h2>
        <FormattedMessage
          id="hosting.lighting_form.lighting_heading"
          defaultMessage="Lighting"
        />
      </h2>
      <p className="text-gray-600">
        <FormattedMessage
          id="hosting.lighting_form.lighting_text"
          defaultMessage="Lighting is one of the most important elements for video and photo shoots. Let guests know what to expect."
        />
      </p>
      <div className="questions-section">
        <h6>
          <FormattedMessage
            id="hosting.lighting_form.natural_light_availability"
            defaultMessage="Is there natural light?"
          />
        </h6>
        <Field
          name='has_natural_light'
          component={ToggleField}
          onChange={() => props.dispatch(
            change('PRODUCTION_LIGHTING_FORM', 'can_block_out_light', null))
          }
          attributes={{
            'data-testing-id': 'has_natural_light',
            name: 'is_available'
          }}
          firstOption={
            {
              id: 'has_natural_light_true',
              value: 'true',
              label: intl.formatMessage(translationMessages.yes)
            }}
          secondOption={
            {
              id: 'has_natural_light_false',
              value: 'false',
              label: intl.formatMessage(translationMessages.no)
            }}
        />
      </div>
      { hasNaturalLight &&
        <div className="margin-xl-top">
          <p className="heavy">
            <FormattedMessage
              id="hosting.lighting_form.block_natural_light"
              defaultMessage="Can natural light be blocked out?"
            />
          </p>
          <p className="text-gray-600">
            <FormattedMessage
              id="hosting.lighting_form.space_curtains_window_coverings"
              defaultMessage="Does the space have blackout curtains, window coverings, etc?"
            />
          </p>
          <Field
            name='can_block_out_light'
            component={ToggleField}
            attributes={{
              'data-testing-id': 'can_block_out_light',
              name: 'is_available'
            }}
            firstOption={
              {
                id: 'can_block_out_light_true',
                value: 'true',
                label: intl.formatMessage(translationMessages.yes)
              }}
            secondOption={
              {
                id: 'can_block_out_light_false',
                value: 'false',
                label: intl.formatMessage(translationMessages.no)
              }}
          />
        </div>
      }
      <div className="questions-section">
        <h6>
          <FormattedMessage
            id="hosting.lighting_form.space_lighting_describe"
            defaultMessage="Describe the lighting in your space"
          />
        </h6>
        <ul className="text-gray-600 list-style-inside no-gutter margin-lg-bottom">
          <li>
            <FormattedMessage
              id="hosting.lighting_form.windows_description"
              defaultMessage="What are the windows like?"
            />
          </li>
          <li>
            <FormattedMessage
              id="hosting.lighting_form.natural_light_controlled"
              defaultMessage="How can the natural light be controlled?"
            />
          </li>
          <li>
            <FormattedMessage
              id="hosting.lighting_form.brightest_time_of_day"
              defaultMessage="What times of the day are the brightest?"
            />
          </li>
        </ul>
        <Field
          name="lighting_description"
          attributes={{ 'data-testing-id': 'lighting_description_field' }}
          component={TextAreaField}
          rows={5}
          maxLength={1000}
        />
      </div>
      <hr className="space-type-line-break"/>
      <div className="text-danger text-small margin-xl-bottom">{error}</div>
      <ButtonNavigation {...props} />
    </form>
  );
};

LightingForm.propTypes = {
  handleSubmit: PropTypes.func,
  error: PropTypes.string,
  dispatch: PropTypes.func,
  parkingToggleFieldValue: PropTypes.string,
  hasNaturalLight: PropTypes.bool,
  intl: intlShape
};

const config = {
  form: 'PRODUCTION_LIGHTING_FORM',
  validate: validateLightingForm,
  onSubmit: onSubmitProductionForms
};

const selector = formValueSelector('PRODUCTION_LIGHTING_FORM');

function mapStateToProps (state) {
  const hasNaturalLight = selector(state, 'has_natural_light');
  const productionDetails = get(state, 'space.data.production_details', {});

  return {
    hasNaturalLight: 'true' === hasNaturalLight,
    initialValues: {
      ...mapBoolToString(productionDetails)
    }
  };
}

export default connect(mapStateToProps)(reduxForm(config)(injectIntl(LightingForm)));
